import type { FactsProblem, FactsSchema } from "./types";

/**
 * Whether an answer from a facts gateway has the shape a screen can draw from. Build round 18
 * (2026-10-07), in `app/data/adminFactsQueries.ts`.
 *
 * Moved here in build round 19 (2026-10-08), when the client's own gateways began to answer with the
 * same schema and the same problems. The admin data module is admin code, which is kept out of the
 * client's entry chunk, so the checks both sides need live beside the contract's types instead.
 *
 * These are not the contract's checks. The database ran those before it stored a schema or refused a
 * value; this is only whether the form has what it needs to draw a control at all, and an answer
 * that fails here is a failed read and never an empty one.
 */

type JsonObject = Record<string, unknown>;

export const isObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);
export const isString = (value: unknown): value is string => typeof value === "string";
export const isNullableString = (value: unknown): value is string | null => value === null || isString(value);
export const isInteger = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value);

const FIELD_TYPES = new Set(["text", "number", "price", "opening_hours", "phone", "email", "url", "list"]);
const ITEM_TYPES = new Set(["text", "number", "price", "phone", "email", "url"]);

export function isFactsProblems(value: unknown): value is FactsProblem[] {
  return Array.isArray(value) && value.every((problem) => isObject(problem) && isString(problem.key) && isString(problem.code));
}

function isPriceOption(value: unknown): boolean {
  return isObject(value) && (value.editable === true || (value.editable === false && typeof value.value === "boolean"));
}

/** Whether a field has what the form is built from: the shape it needs to draw a control. */
function isFieldShape(value: unknown, item: boolean): boolean {
  if (!isObject(value) || !isString(value.key) || !isString(value.label) || typeof value.required !== "boolean") {
    return false;
  }
  if (value.help !== undefined && !isString(value.help)) {
    return false;
  }
  if (!isString(value.type) || !(item ? ITEM_TYPES : FIELD_TYPES).has(value.type)) {
    return false;
  }
  if (!item && typeof value.editable !== "boolean") {
    return false;
  }
  switch (value.type) {
    case "text":
      return (
        isInteger(value.max_length) &&
        (value.allowed_values === undefined ||
          (Array.isArray(value.allowed_values) && value.allowed_values.every(isString)))
      );
    case "number":
      return typeof value.min === "number" && typeof value.max === "number" && typeof value.integer === "boolean";
    case "price":
      return typeof value.max === "number" && isPriceOption(value.from) && isPriceOption(value.incl_vat);
    case "list":
      return (
        Array.isArray(value.item_fields) &&
        value.item_fields.length > 0 &&
        value.item_fields.every((field) => isFieldShape(field, true)) &&
        isInteger(value.min_items) &&
        isInteger(value.max_items)
      );
    default:
      return true;
  }
}

export function isFactsSchema(value: unknown): value is FactsSchema {
  return (
    isObject(value) &&
    value.version === 1 &&
    Array.isArray(value.fields) &&
    value.fields.every((field) => isFieldShape(field, false))
  );
}

/**
 * The problems in a `PORTAL_SITE_FACTS_INVALID` or `PORTAL_SITE_FACTS_LOCKED` refusal. PostgREST hands
 * the exception's detail over as `details`, a string, and the gateway writes the problems there as a
 * JSON array. Anything else is no problems at all rather than a guess, and the refusal's own sentence
 * still says what happened.
 */
export function problemsFromDetails(details: unknown): FactsProblem[] {
  let parsed: unknown = details;
  if (isString(details)) {
    try {
      parsed = JSON.parse(details);
    } catch {
      return [];
    }
  }
  return isFactsProblems(parsed) ? parsed : [];
}
